import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Host,
  Input,
  OnChanges,
  Optional,
  Output,
  Renderer2,
  Self,
  SimpleChanges,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { map, startWith } from 'rxjs';

@Directive({
  selector: '[appBubblePagination]',
  standalone: true,
})
export class BubblePaginationDirective implements AfterViewInit, OnChanges {
  @Output() pageIndexChangeEmitter: EventEmitter<number> =
    new EventEmitter<number>();

  @Input() showFirstButton = true;
  @Input() showLastButton = true;
  @Input() renderButtonsNumber = 2;
  @Input() appCustomLength: number = 0;
  @Input() hideDefaultArrows = false;

  /** Important – external pageIndex */
  @Input() appPageIndex: number = 0;

  private bubbleContainerRef!: HTMLElement;
  private buttonsRef: HTMLElement[] = [];

  private isInternalChange = false; // 🔥 Prevent duplicate calls

  constructor(
    @Host() @Self() @Optional() private readonly matPag: MatPaginator,
    private elementRef: ElementRef,
    private ren: Renderer2
  ) {}

  ngAfterViewInit(): void {
    this.styleDefaultPagination();
    this.createBubbleDivRef();
    this.renderButtons();

    /** 🔥 Highlight saved page index when paginator initialized */
    setTimeout(() => {
      this.applyExternalPageIndex(this.appPageIndex);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    /** total length changed */
    if (changes['appCustomLength'] && !changes['appCustomLength'].firstChange) {
      this.removeButtons();
      this.renderButtons();
      this.applyExternalPageIndex(this.matPag.pageIndex);
    }

    /** external pageIndex updated */
    if (changes['appPageIndex'] && !changes['appPageIndex'].firstChange) {
      this.applyExternalPageIndex(changes['appPageIndex'].currentValue);
    }
  }

  /** =======================================================================
      APPLY EXTERNAL PAGE INDEX (NO DUPLICATE CHANGE / NO EVENTS)
     ======================================================================= */
  private applyExternalPageIndex(newIndex: number) {
    if (this.isInternalChange) return; // internal update → ignore
    if (!this.buttonsRef.length) return;

    this.isInternalChange = true;

    const prev = this.matPag.pageIndex;
    this.matPag.pageIndex = newIndex;

    /** highlight UI */
    this.changeActiveButtonStyles(prev, newIndex);

    this.isInternalChange = false;
  }

  /** =======================================================================
      RENDER BUTTONS + SETUP PAGINATOR OPS
     ======================================================================= */
  private renderButtons(): void {
    this.buildButtons();

    let prevPageSize = this.matPag.pageSize;

    this.matPag.page
      .pipe(
        startWith({
          previousPageIndex: 0,
          pageIndex: this.matPag.pageIndex,
          pageSize: this.matPag.pageSize,
          length: this.appCustomLength,
        }),
        map((event) => {
          const pageSizeChanged = event.pageSize !== prevPageSize;
          prevPageSize = event.pageSize;

          return {
            prev: event.previousPageIndex ?? 0,
            curr: event.pageIndex,
            pageSizeChanged,
          };
        })
      )
      .subscribe(({ prev, curr, pageSizeChanged }) => {
        if (this.isInternalChange) return; // skip if programmatic

        if (pageSizeChanged) {
          this.removeButtons();
          this.buildButtons();
        }

        this.changeActiveButtonStyles(prev, curr);
      });
  }

  /** =======================================================================
      UPDATE ACTIVE BUBBLE — NOW ONLY ONE CALL
     ======================================================================= */
  private changeActiveButtonStyles(previousIndex: number, newIndex: number) {
    if (!this.buttonsRef.length) return;

    const prevBtn = this.buttonsRef[previousIndex];
    const newBtn = this.buttonsRef[newIndex];

    if (prevBtn) this.ren.removeClass(prevBtn, 'g-bubble__active');
    if (newBtn) this.ren.addClass(newBtn, 'g-bubble__active');

    this.buttonsRef.forEach((b) => this.ren.setStyle(b, 'display', 'none'));

    const range = this.renderButtonsNumber;

    const start = Math.max(0, newIndex - range);
    const end = Math.min(this.buttonsRef.length - 1, newIndex + range);

    for (let i = start; i <= end; i++) {
      this.ren.setStyle(this.buttonsRef[i], 'display', 'flex');
    }
  }

  /** ======================================================================= */
  private styleDefaultPagination() {
    const nativeElement = this.elementRef.nativeElement;

    const pageSizeContainer =
      nativeElement.querySelector('.mat-mdc-paginator-page-size');

    if (pageSizeContainer) {
      const labels = pageSizeContainer.querySelectorAll('label, span');
      labels.forEach((label) => {
        if (label.textContent?.toLowerCase().includes('items per page')) {
          this.ren.setStyle(label, 'display', 'none');
        }
      });
    }

    const rangeLabel =
      nativeElement.querySelector('.mat-mdc-paginator-range-label');
    if (rangeLabel) {
      this.ren.setStyle(rangeLabel, 'display', 'none');
    }
  }

  private createBubbleDivRef(): void {
    const actionContainer = this.elementRef.nativeElement.querySelector(
      'div.mat-mdc-paginator-range-actions'
    );
    const nextBtn = this.elementRef.nativeElement.querySelector(
      'button.mat-mdc-paginator-navigation-next'
    );

    this.bubbleContainerRef = this.ren.createElement('div');
    this.ren.addClass(this.bubbleContainerRef, 'g-bubble-container');

    this.ren.insertBefore(actionContainer, this.bubbleContainerRef, nextBtn);
  }

  /** ======================================================================= */
  private buildButtons(): void {
    const total = this.appCustomLength ?? 0;
    const size = this.matPag.pageSize ?? 1;
    const pages = total > 0 ? Math.ceil(total / size) : 1;

    this.removeButtons();

    for (let i = 0; i < pages; i++) {
      this.buttonsRef.push(this.createButton(i));
    }

    if (this.buttonsRef.length) {
      this.ren.addClass(this.buttonsRef[0], 'g-bubble__active');
    }
  }

  private removeButtons(): void {
    if (!this.bubbleContainerRef) return;

    this.buttonsRef.forEach((btn) =>
      this.ren.removeChild(this.bubbleContainerRef, btn)
    );

    this.buttonsRef = [];
  }

  private createButton(i: number): HTMLElement {
    const btn = this.ren.createElement('div');
    const text = this.ren.createText(String(i + 1));

    this.ren.addClass(btn, 'g-bubble');
    this.ren.setStyle(btn, 'margin-right', '8px');
    this.ren.appendChild(btn, text);
    this.ren.setStyle(btn, 'display', 'none');

    this.ren.listen(btn, 'click', () => {
      this.isInternalChange = true;

      const prev = this.matPag.pageIndex;
      this.matPag.pageIndex = i;

      // Emit default paginator event
      this.matPag['_emitPageEvent'](prev);

      this.changeActiveButtonStyles(prev, i);

      this.pageIndexChangeEmitter.emit(i);

      this.isInternalChange = false;
    });

    this.ren.appendChild(this.bubbleContainerRef, btn);
    return btn;
  }
}
